/**
 * The choices a run did not make that would give a mostly silent tune or bass its notes.
 *
 * "Follow the tune down" and "Follow the bass up" are off until ticked, so a score whose top
 * staff drops out while another takes the tune comes back with the tune's player waiting too.
 * That is the source read as written, but nothing on the result says a box would change it,
 * and a reader hearing a first violin rest through most of a prelude blames the arranger. The
 * pipeline offers a choice only where the line is silent for more than half the passage and
 * the choice would give it more than a point more of the passage to play (`rk_keep_offers`),
 * and says how much, so a small gain reads as small.
 *
 * Taking an offer ticks its box and arranges again with the toolbar as it stands, exactly as
 * ticking the box and pressing Arrange would; nothing is changed that the toolbar does not show.
 */

import type { KeptOffer } from "@app/shared-types";
import { rate } from "../lib/format";
import { choiceLabel } from "./ArrangeChoices";

/** Where each line is read from, and where a choice reads it from while that staff rests. */
const SAID: Readonly<Record<KeptOffer["line"], { staff: string; next: string }>> = {
  tune: { staff: "top", next: "down" },
  bass: { staff: "lowest", next: "up" },
};

export default function ArrangeOffers({
  offers,
  disabled,
  onTake,
}: {
  readonly offers: readonly KeptOffer[];
  /** While a run is going, where Arrange itself could not start one, or while the start is
   *  the tune, which takes none of the choices an offer ticks. */
  readonly disabled: boolean;
  onTake(choice: KeptOffer["choice"]): void;
}): JSX.Element | null {
  if (offers.length === 0) return null;
  return (
    <div className="shrink-0 space-y-1 border-t border-shell-edge bg-shell-bar px-2.5 py-1.5 text-[11px] leading-relaxed text-shell-text">
      {offers.map((offer) => {
        const { staff, next } = SAID[offer.line];
        return (
          <p key={offer.choice} className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span className="rounded border-l-2 border-unread bg-shell-key/60 px-2 py-0.5">
              The {offer.line} sounds for {rate(offer.now, 0)} of the passage: the {staff} staff is
              silent for the rest. Taken from the next staff {next} while it is, it would sound for{" "}
              {rate(offer.then, 0)}.
            </span>
            <button
              type="button"
              onClick={() => onTake(offer.choice)}
              disabled={disabled}
              className="h-6 shrink-0 rounded border border-shell-seam px-2.5 text-[11px] text-shell-text hover:bg-shell-key disabled:text-shell-dim"
            >
              {choiceLabel(offer.choice)} and arrange again
            </button>
          </p>
        );
      })}
    </div>
  );
}
